import { useMediaQuery } from '@mantine/hooks';
import { useMantineTheme } from '@mantine/core';

/**
 * レイアウトモード
 * - desktop: lg以上（3カラム）
 * - tablet: md（2カラム）
 * - mobile: sm未満（1カラム）
 */
export type LayoutMode = 'desktop' | 'tablet' | 'mobile';

/**
 * ブレークポイント状態
 */
interface BreakpointState {
  /** xl (88em以上) */
  isXl: boolean;
  /** lg (75em以上) */
  isLg: boolean;
  /** md (62em以上) */
  isMd: boolean;
  /** sm (48em以上) */
  isSm: boolean;
  /** xs (36em以上) */
  isXs: boolean;
}

/**
 * レスポンシブレイアウト状態
 */
export interface ResponsiveLayoutState extends BreakpointState {
  /** 現在のレイアウトモード */
  layoutMode: LayoutMode;
  /** 3カラム表示かどうか (lg以上) */
  isThreeColumn: boolean;
  /** 2カラム表示かどうか (md) */
  isTwoColumn: boolean;
  /** 1カラム表示かどうか (sm未満) */
  isSingleColumn: boolean;
  /** サイドバーをインラインで表示するか (lg以上) */
  showInlineSidebar: boolean;
  /** 右パネルを表示するか (md以上) */
  showRightPanel: boolean;
}

/**
 * レスポンシブレイアウト判定Hook
 *
 * 画面幅に基づいてブレークポイントを判定し、
 * 現在のレイアウトモードと表示フラグを提供する。
 *
 * @returns ResponsiveLayoutState
 *
 * @example
 * ```tsx
 * const { layoutMode, isThreeColumn, showInlineSidebar } = useResponsiveLayout();
 *
 * if (isThreeColumn) {
 *   // 3カラムレイアウト（lg以上）
 * }
 * ```
 */
export function useResponsiveLayout(): ResponsiveLayoutState {
  const theme = useMantineTheme();

  // 各ブレークポイントの判定
  const isXl = useMediaQuery(`(min-width: ${theme.breakpoints.xl})`) ?? false;
  const isLg = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`) ?? false;
  const isMd = useMediaQuery(`(min-width: ${theme.breakpoints.md})`) ?? false;
  const isSm = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`) ?? false;
  const isXs = useMediaQuery(`(min-width: ${theme.breakpoints.xs})`) ?? false;

  // レイアウトモードの算出
  const layoutMode: LayoutMode = isLg ? 'desktop' : isMd ? 'tablet' : 'mobile';

  // カラム表示の判定
  const isThreeColumn = isLg; // lg以上で3カラム
  const isTwoColumn = isMd && !isLg; // mdで2カラム
  const isSingleColumn = !isMd; // sm未満で1カラム

  // 表示フラグ
  const showInlineSidebar = isLg; // lg以上でサイドバーをインライン表示
  const showRightPanel = isMd; // md以上で右パネルを表示

  return {
    // ブレークポイント状態
    isXl,
    isLg,
    isMd,
    isSm,
    isXs,
    // レイアウトモード
    layoutMode,
    // カラム表示判定
    isThreeColumn,
    isTwoColumn,
    isSingleColumn,
    // 表示フラグ
    showInlineSidebar,
    showRightPanel,
  };
}
